import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { EntityIcon } from '@components/core-ui/EntityIcon/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import { Select } from '@components/core-ui/form-elements/Select';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { Header } from '@components/Header';
import AddIcon from '@components/icons/Add.svg';
import PencilIcon from '@components/icons/Pencil.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { getTranscriptUnlockTypeName } from '@helpers/getTranscriptUnlockTypeName';
import { pluralize } from '@helpers/pluralize';
import { useAuthContext } from '@providers/AuthProvider';
import { routes } from '@routes';
import { usePageContext } from '../../providers/PageProvider';
import { AvatarBlock } from './styles/AvatarBlock';
import { CellContent } from './styles/CellContent';
import { CellContentFlex } from './styles/CellContentFlex';
import { MainContent } from './styles/MainContent';

export const Content = () => {
  const {
    isLoading,
    isLoadError,
    retryLoad,
    search,
    setSearch,
    removeAssignee,
    editTranscriptUnlockType,
    editAllTranscriptUnlockTypes,
    editBackupRecordingFlag,
    setGlobalAS1Backup,
    unlockAITranscripts,
    onAddUserClick,
    assignedCount,
    unassignedCount,
    subscription,
    plan,
    globalLicensesBackupEnabledValue,
    openAS1BackupTerms,
  } = usePageContext();
  const { organization } = useAuthContext();

  const renderAvatar = (member: { name: string; lastname: string; avatar?: string | null }) => {
    if (!member) {
      return '';
    }
    return (
      <AvatarBlock className="flex-shrink-0">
        <EntityIcon
          className="flex-shrink-0"
          name={`${member.name} ${member.lastname}`}
          src={member.avatar ?? undefined}
        />
      </AvatarBlock>
    );
  };

  const headerLeftSideContent = [];
  if (organization?.permissions?.manageLicenses) {
    headerLeftSideContent.push(
      <div className="flex flex-row">
        <OutlinedButton
          key="button"
          className="w-[90px] gap-[5px] mr-3 justify-center"
          disabled={(unassignedCount ?? 0) < 1}
          onClick={() => onAddUserClick(null)}
        >
          + Add User
        </OutlinedButton>
      </div>,
    );
  }

  const header = (
    <>
      <Header
        title={'Subscription Details'}
        setSearch={setSearch}
        search={search}
        leftSideContent={headerLeftSideContent}
        backRoute={routes.billingAccount.make(organization?.slug ?? '')}
      />

      <LinearProgressLoader active={isLoading} />
      {isLoadError ? (
        <div className="m-4 flex items-center gap-3 text-xs text-[var(--color-text-muted)]" role="alert">
          <span>Failed to load subscription.</span>
          <TextButton type="button" onClick={retryLoad}>
            Retry
          </TextButton>
        </div>
      ) : null}
    </>
  );

  if (!plan || !subscription) {
    return header;
  }

  return (
    <>
      {header}
      <MainContent>
        <div className="details">
          <div className="left">
            <div className="name">{plan.name}</div>
            <div className="price">${Number(plan.price).toFixed(2)} Per License Monthly</div>
          </div>
          <div className="divider"></div>
          <div className="count">
            You have{' '}
            <span className="bold">
              {unassignedCount} out of {(assignedCount ?? 0) + (unassignedCount ?? 0)}
            </span>{' '}
            {pluralize((assignedCount ?? 0) + (unassignedCount ?? 0), 'license', 'licenses')} available to assign
          </div>
          <div className="divider"></div>
          {subscription?.allowedToChooseTranscriptUnlockType ? (
            <OutlinedButton onClick={() => editAllTranscriptUnlockTypes()}>Update all Unlock types</OutlinedButton>
          ) : (
            <OutlinedButton onClick={() => unlockAITranscripts()}>Unlock AI Transcripts</OutlinedButton>
          )}
          {organization?.featureFlags?.AS_ONE_BACKUP_RECORDING ? (
            <>
              <div className="divider"></div>
              <Select
                className="select-wrapper"
                onClick={
                  !organization?.asOneBackupRecordingTermsAccepted
                    ? (e) => {
                        e.stopPropagation();
                        openAS1BackupTerms();
                      }
                    : undefined
                }
                onChange={(_, v) => setGlobalAS1Backup(v)}
                label={`Global AS1 Backup: ${globalLicensesBackupEnabledValue}`}
                options={[
                  { label: 'On', value: '1' },
                  { label: 'Off', value: '0' },
                ]}
              />
            </>
          ) : null}
        </div>

        <TableWrapper>
          <Table>
            <Thead>
              <Tr>
                <Th>ID</Th>
                <Th>Name</Th>
                <Th style={{ width: '50%' }}>Contacts</Th>
                <Th style={{ width: '50%' }}>
                  <div className="flex gap-2 align-middle">Transcript Unlock Type</div>
                </Th>
                {organization?.featureFlags?.AS_ONE_BACKUP_RECORDING ? (
                  <Th style={{ width: '50%' }}>
                    <div className="flex gap-2 align-middle">AS1 Backup</div>
                  </Th>
                ) : null}
                <Th style={{ width: '1%' }}></Th>
              </Tr>
            </Thead>
            <Tbody>
              {(subscription?.Licenses ?? [])
                .filter(
                  (license) =>
                    license.id.includes(search.toLowerCase()) ||
                    `${license.Assignee?.User?.name} ${license.Assignee?.User?.lastname}`
                      .toLowerCase()
                      .includes(search.toLowerCase()) ||
                    license.Assignee?.User?.email.toLowerCase().includes(search.toLowerCase()) ||
                    license.Assignee?.User?.phone.toLowerCase().includes(search.toLowerCase()),
                )
                .map((data) => (
                  <Tr key={data.id}>
                    <Td>
                      <CellContent>{data.id}</CellContent>
                    </Td>
                    <Td>
                      {!data.Assignee?.User ? (
                        <CellContent>Unassigned</CellContent>
                      ) : (
                        <CellContentFlex>
                          {renderAvatar(data.Assignee.User)}
                          {data.Assignee.User?.name} {data.Assignee.User?.lastname}
                        </CellContentFlex>
                      )}
                    </Td>
                    <Td>
                      {!data.Assignee?.User ? (
                        <CellContent></CellContent>
                      ) : (
                        <CellContent>
                          <div>{data.Assignee.User.email}</div>
                          <div>{data.Assignee.User.phone}</div>
                        </CellContent>
                      )}
                    </Td>
                    <Td>
                      <CellContent>
                        <div className="flex gap-1 align-middle">
                          {getTranscriptUnlockTypeName(data.transcriptUnlockType)}
                          {subscription?.allowedToChooseTranscriptUnlockType && data.assigneeId ? (
                            <IconButtonClean
                              type="button"
                              title="Edit transcript unlock type"
                              className="pencil"
                              onClick={() => editTranscriptUnlockType(data.id, data.transcriptUnlockType ?? '')}
                            >
                              <PencilIcon />
                            </IconButtonClean>
                          ) : null}
                        </div>
                      </CellContent>
                    </Td>
                    {organization?.featureFlags?.AS_ONE_BACKUP_RECORDING ? (
                      <Td>
                        <CellContent>
                          <div className="flex gap-1 align-middle">
                            {data.asOneBackupRecordingAllowed ? 'On' : 'Off'}
                            {data.assigneeId ? (
                              <IconButtonClean
                                type="button"
                                title="Edit backup recording"
                                className="pencil"
                                onClick={() => editBackupRecordingFlag(data.id, data.asOneBackupRecordingAllowed)}
                              >
                                <PencilIcon />
                              </IconButtonClean>
                            ) : null}
                          </div>
                        </CellContent>
                      </Td>
                    ) : null}
                    <Td>
                      {!organization?.permissions?.manageLicenses ? (
                        <span></span>
                      ) : !data.Assignee?.User ? (
                        <CellContent className="flex-shrink-0 min-w-[22px]">
                          <IconButtonClean
                            type="button"
                            title="Add user"
                            className="add"
                            onClick={() => onAddUserClick(data.id)}
                          >
                            <AddIcon />
                          </IconButtonClean>
                        </CellContent>
                      ) : (
                        <CellContent className="flex-shrink-0 min-w-[22px]">
                          <IconButtonDelete
                            type="button"
                            title="Remove assignee"
                            className="delete"
                            onClick={() => removeAssignee(data.id)}
                          >
                            <TrashBin />
                          </IconButtonDelete>
                        </CellContent>
                      )}
                    </Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>
        </TableWrapper>
      </MainContent>
    </>
  );
};
